import { Link } from 'react-router-dom';
import { Head } from 'vite-react-ssg';
import FooterSection from '../../components/FooterSection';
import { WA_NUMBER } from '../../constant/detail';

const relatedServices = [
  { label: 'Jasa Maintenance Kolam Renang Bali', to: '/jasa-maintenance-kolam-renang-bali' },
  { label: 'Pool Maintenance Ubud', to: '/jasa-perawatan-kolam-renang-ubud' },
  { label: 'Pool Chemical Supply Bali', to: '/jual-chemical-pool-bali' },
  { label: 'Pool Filter Installation Bali', to: '/pasang-filter-kolam-renang-bali' },
  { label: 'Pool Equipment Bali', to: '/equipment-pool-bali' },
];

const WA_MESSAGE = encodeURIComponent(
  'Hi, I need pool maintenance service in Bali'
);

const faqData = [
  {
    q: 'How much does pool maintenance cost in Bali?',
    a: 'Pool maintenance costs in Bali depend on pool size, visit frequency, and current pool condition. We offer weekly and monthly packages at competitive rates with no hidden fees. Contact us via WhatsApp for a tailored quote.',
  },
  {
    q: 'How often does a pool in Bali need maintenance?',
    a: "In Bali's tropical climate, we recommend at least weekly maintenance to keep water chemistry balanced and prevent algae growth. Pools surrounded by trees or heavily used villa pools may need twice-weekly visits.",
  },
  {
    q: 'Do you service pools in Ubud specifically?',
    a: 'Yes. Ubud and the surrounding villages — Peliatan, Mas, Sayan, Kedewatan, Tegallalang, Payangan, and Singakerta — are among our most active service areas. Our stores in Lodtunduh and Petulu are both within 15–20 minutes of most Ubud properties.',
  },
  {
    q: 'Can you fix a green or cloudy pool?',
    a: 'Absolutely. Green water caused by algae and cloudy water from chemical imbalance are common in Bali. We handle the full recovery process: shock treatment, thorough mechanical cleaning, water chemistry correction, and filter backwash. Most pools clear up within 1–3 days.',
  },
  {
    q: 'Do you service hotel and villa pools?',
    a: 'Yes — villa and resort pools make up the majority of our clients. We work around guest schedules and provide a WhatsApp report after every visit so property managers can stay informed remotely.',
  },
];

const serviceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Pool Maintenance in Bali',
  description:
    'Professional pool maintenance in Bali and Ubud. Regular cleaning, water chemistry balancing, pump and filter servicing for villas, hotels, and resorts. Serving all of Bali.',
  provider: {
    '@type': 'LocalBusiness',
    name: 'Pool Shop Surya Jaya Teknik',
    telephone: '+628123618911',
    url: 'https://poolshopsuryajayateknik.com',
    address: [
      {
        '@type': 'PostalAddress',
        streetAddress: 'Jalan Raya Andong, Petulu',
        addressLocality: 'Gianyar',
        addressRegion: 'Bali',
        addressCountry: 'ID',
      },
      {
        '@type': 'PostalAddress',
        streetAddress: 'Jl. Raya Kengetan, Lodtunduh',
        addressLocality: 'Gianyar',
        addressRegion: 'Bali',
        addressCountry: 'ID',
      },
    ],
  },
  areaServed: [
    { '@type': 'City', name: 'Ubud' },
    { '@type': 'City', name: 'Gianyar' },
    { '@type': 'AdministrativeArea', name: 'Bali, Indonesia' },
  ],
  openingHoursSpecification: {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    opens: '08:00',
    closes: '17:00',
  },
  url: 'https://poolshopsuryajayateknik.com/pool-maintenance-bali',
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqData.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
};

const PoolMaintenanceBali = () => {
  return (
    <>
      <Head>
        <title>Pool Maintenance in Bali &amp; Ubud | Surya Jaya Teknik</title>
        <meta
          name="description"
          content="Professional pool maintenance in Bali and Ubud. Regular cleaning, water chemistry, pump & filter service for villas and hotels. Call us today!"
        />
        <link rel="canonical" href="https://poolshopsuryajayateknik.com/pool-maintenance-bali" />
        <meta property="og:title" content="Pool Maintenance in Bali & Ubud | Surya Jaya Teknik" />
        <meta property="og:description" content="Professional pool maintenance in Bali and Ubud. Regular cleaning, water chemistry, pump & filter service for villas and hotels." />
        <meta property="og:image" content="https://poolshopsuryajayateknik.com/img/blog/img-2.jpg" />
        <meta property="og:url" content="https://poolshopsuryajayateknik.com/pool-maintenance-bali" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Pool Maintenance in Bali & Ubud | Surya Jaya Teknik" />
        <meta name="twitter:description" content="Professional pool maintenance in Bali and Ubud. Regular cleaning, water chemistry, pump & filter service for villas and hotels." />
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Head>

      <main className="mt-16 bg-white text-gray-800">
        {/* Hero */}
        <section className="bg-tertiary text-white py-16 px-6 text-center">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-4">
              Professional Pool Maintenance in Bali
            </h1>
            <p className="text-lg md:text-xl text-primary/80 mb-2">
              Trusted &bull; Experienced &bull; Available Island-Wide
            </p>
            <p className="text-base text-primary/70 mb-8">
              Green water, cloudy pool, or equipment issues? Surya Jaya Teknik provides
              complete pool maintenance in Bali for villas, hotels, and resorts. Our
              technicians handle everything from routine cleaning and water chemistry to
              pump servicing and algae treatment — with a WhatsApp report after every visit.
            </p>
            <a
              href={`https://wa.me/${WA_NUMBER}?text=${WA_MESSAGE}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-primary hover:opacity-90 text-tertiary font-semibold py-3 px-8 rounded-full text-lg transition"
            >
              Get a Free Quote on WhatsApp
            </a>
          </div>
        </section>

        {/* Why Bali pools need maintenance */}
        <section className="max-w-4xl mx-auto py-14 px-6">
          <h2 className="text-2xl font-bold text-tertiary mb-4">
            Why Pool Maintenance in Bali Requires More Attention
          </h2>
          <p className="mb-4 leading-relaxed">
            Bali's tropical climate — heat, humidity, and abundant vegetation — creates
            conditions where pool water deteriorates faster than in temperate climates.
            Frangipani flowers, palm fronds, and tropical leaves fall into pools daily,
            consuming chlorine and clogging filters. UV radiation breaks down free chlorine
            rapidly on sunny days, leaving pools vulnerable to algae growth within 48–72
            hours if circulation and chemical levels are not maintained.
          </p>
          <p className="leading-relaxed">
            For villa and resort pools in areas like Ubud, Tegallalang, Kedewatan, and
            Payangan — where pools are often set within lush garden environments — the
            organic load is even higher. At Surya Jaya Teknik, we have spent over a decade
            servicing pools specifically in this environment. We understand the local
            water sources (borehole water mineral content varies significantly between
            areas), the seasonal patterns (rainy season pH swings, dry season evaporation
            rates), and the expectations of international guests who use these pools.
          </p>
        </section>

        {/* Pool Maintenance in Ubud */}
        <section className="bg-primary/10 py-14 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-tertiary mb-4">
              Pool Maintenance in Ubud — Our Core Area
            </h2>
            <p className="mb-4 leading-relaxed">
              Our two stores — in Petulu (Jalan Raya Andong) and Lodtunduh (Jl. Raya
              Kengetan) — place us within 10–20 minutes of the vast majority of Ubud
              properties. This proximity means we can offer same-day response for
              urgent pool issues, and our technicians are familiar with the specific
              conditions of each sub-area: the higher organic debris load in
              Campuhan and Sayan, the harder borehole water in Kedewatan and Payangan,
              and the shaded pool sections common in Penestanan and Sanggingan.
            </p>
            <p className="leading-relaxed">
              Whether you manage a boutique villa in the rice fields, a spiritual retreat
              near Campuhan ridge, or a hotel along Monkey Forest Road, our pool
              maintenance service in Ubud is scheduled around your operation — not the
              other way around.
            </p>
            <div className="mt-6">
              <Link
                to="/jasa-perawatan-kolam-renang-ubud"
                className="inline-block bg-tertiary text-white text-sm font-semibold py-2 px-6 rounded-full hover:opacity-90 transition"
              >
                View Full Ubud Pool Maintenance Page →
              </Link>
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="max-w-4xl mx-auto py-14 px-6">
          <h2 className="text-2xl font-bold text-tertiary mb-4">
            What Our Pool Maintenance Service Includes
          </h2>
          <p className="mb-6 leading-relaxed">
            Every maintenance visit is comprehensive — we do not cut corners or skip steps
            when running short on time. Our standard visit covers:
          </p>
          <ul className="space-y-3">
            {[
              'Full vacuum of pool floor and walls',
              'Brushing of walls, steps, and waterline (removes biofilm and staining)',
              'Surface skimming and skimmer basket clearing',
              'Complete water chemistry test: pH, free chlorine, total alkalinity, calcium hardness',
              'Chemical dosing and adjustment as needed',
              'Filter pressure check; backwash if pressure exceeds threshold',
              'Pump and equipment inspection',
              'WhatsApp report with water test readings after every visit',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="text-tertiary font-bold mt-0.5">✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Areas served */}
        <section className="bg-primary/10 py-14 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-tertiary mb-4">
              Areas We Serve Across Bali
            </h2>
            <p className="mb-6 leading-relaxed">
              Based in Gianyar regency, we serve properties across Bali with a focus on
              the following areas:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
              {[
                'Ubud', 'Tegallalang', 'Payangan',
                'Kedewatan', 'Sayan', 'Campuhan',
                'Peliatan', 'Mas', 'Lodtunduh',
                'Petulu', 'Penestanan', 'Singakerta',
                'Gianyar', 'Tabanan', 'Bangli',
                'Klungkung',
              ].map((area, i) => (
                <div
                  key={i}
                  className="bg-white border border-primary/30 rounded-lg px-4 py-2 text-center text-sm font-medium text-tertiary"
                >
                  {area}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Us */}
        <section className="max-w-4xl mx-auto py-14 px-6">
          <h2 className="text-2xl font-bold text-tertiary mb-6">
            Why Choose Surya Jaya Teknik for Pool Maintenance in Bali?
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                title: 'Local Expertise, 10+ Years',
                desc: "We have serviced hundreds of pools specifically in Bali's tropical environment. We know what local water sources, seasons, and pool types require.",
              },
              {
                title: 'Transparent Pricing',
                desc: 'No hidden fees. We give you a price estimate before work starts and stick to it.',
              },
              {
                title: 'Digital Reports via WhatsApp',
                desc: 'After every visit, you receive a complete water test reading and pool condition report — perfect for remote property managers.',
              },
              {
                title: 'Flexible Scheduling',
                desc: 'Available 6 days a week. We work around your guest schedule and property operations.',
              },
              {
                title: 'Satisfaction Guarantee',
                desc: "If your pool isn't right after a visit, we come back at no extra charge until it is.",
              },
              {
                title: 'Chemical + Equipment Supply',
                desc: 'We also stock a full range of pool chemicals and equipment at our stores — convenient for managers who prefer to handle some maintenance in-house.',
              },
            ].map((item, i) => (
              <div key={i} className="bg-white border border-primary rounded-xl p-5 shadow-sm">
                <h3 className="font-semibold text-tertiary mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-primary/10 py-14 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-tertiary mb-6">
              Frequently Asked Questions — Pool Maintenance in Bali
            </h2>
            <div className="space-y-5">
              {faqData.map((item, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-xl p-5">
                  <h3 className="font-semibold text-tertiary mb-2">{item.q}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-tertiary text-white py-16 px-6 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold mb-4">
              Ready to Book Pool Maintenance in Bali?
            </h2>
            <p className="text-primary/80 mb-8">
              Send us a WhatsApp message with your location and pool details. We will get
              back to you quickly with availability and pricing. Same-day response, no
              obligation.
            </p>
            <a
              href={`https://wa.me/${WA_NUMBER}?text=${WA_MESSAGE}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-primary hover:opacity-90 text-tertiary font-semibold py-3 px-10 rounded-full text-lg transition"
            >
              Contact Us via WhatsApp
            </a>
          </div>
        </section>

        {/* Related Services */}
        <section className="max-w-4xl mx-auto py-10 px-6">
          <h2 className="text-lg font-bold text-tertiary mb-4">Our Services</h2>
          <div className="flex flex-wrap gap-3">
            {relatedServices.map(({ label, to }) => (
              <Link
                key={to}
                to={to}
                className="text-sm font-medium text-primary border border-primary/40 px-4 py-2 rounded-full hover:bg-primary/10 transition"
              >
                {label}
              </Link>
            ))}
          </div>
        </section>

        <FooterSection />
      </main>
    </>
  );
};

export default PoolMaintenanceBali;
